import React, {forwardRef} from 'react';
import {Box, CircularProgress, Tooltip} from "@mui/material";
import {IconButton, Typography} from "@mui/joy";
import {
    Graph2Rounded,
    OpenInNewRounded,
    ReadMoreRounded,
} from '@mui-symbols-material/w300';
import {useNavigate} from "react-router-dom";
import _ from "lodash";
import {BoxIcon, SubNodeArc} from '@dreebit-registry/vsm-microservice-components';
import {InstalledBaseNode, IoTConnection, IoTSystemConnection} from "../../codegen/graphql";
import {statusIconFactory} from "../../utils/overallStatusIconFactory";
import SignalStrengthIcon from "../SignalStrengthIcon";
import InstalledBaseNodeTypeIcon from "../InstalledBaseNodeTypeIcon";
import {useTranslation} from "react-i18next";
import {useQuery} from "@apollo/client";
import {AssetNetworkInfo} from "../../graphql/queries/assetIoTConnectionWidget";
import {SystemIoTConnectionStatus, SystemNetworkInfo} from "../../graphql/queries/systemIoTConnectionWidget";
import {getInstalledBaseNodeTypeName} from "../../utils/getInstalledBaseNodeTypeName";

interface Props {
    title?: string,
    selected?: boolean,
    // The column's selected node shown pinned above the list (it is not on the
    // first page); marked so list-row lookups can tell it from the list rows.
    pinned?: boolean,
    contextActive?: boolean,
    onClick?: () => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    onOpenDetailView?: () => void;
    // Dedicated detail view of ASSET/SYSTEM nodes (getNodeDetailRoute). When set,
    // the row button navigates there instead of toggling the detail panel.
    detailRoute?: string;
    // true while this node's detail panel is open — the open button stays
    // visible and acts as a close toggle.
    detailOpen?: boolean;
    onContextMenu?: (event: React.MouseEvent<HTMLDivElement>, node: InstalledBaseNode) => void;
    node: InstalledBaseNode;
    style?: React.CSSProperties;
}

const InstalledBaseTreeItem = forwardRef<HTMLDivElement, Props>(({
                                                                     title,
                                                                     selected,
                                                                     pinned,
                                                                     contextActive,
                                                                     node,
                                                                     onOpenDetailView,
                                                                     detailRoute,
                                                                     detailOpen,
                                                                     onClick,
                                                                     onKeyDown,
                                                                     onContextMenu,
                                                                     style
                                                                 }, ref) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const iotConnection: IoTConnection | undefined = _.get(node, 'assetLocation.iotConnection') || undefined;
    const assetLocationId = _.get(node, 'assetLocation.id');
    const systemId = node.system?.id;

    const { data: assetNetworkData, loading: assetNetworkLoading } = useQuery(AssetNetworkInfo, {
        variables: { id: assetLocationId },
        skip: !assetLocationId || !iotConnection?.id,
        // A failed network lookup nulls the whole iotConnection — keep it out of the cache.
        fetchPolicy: 'no-cache',
    });

    // System IoT data is deliberately NOT part of the node list query — the IoT
    // resolvers are slow and would delay the whole column, so system rows fetch
    // their status (and, only when connected, network info) deferred per row.
    const { data: systemStatusData } = useQuery(SystemIoTConnectionStatus, {
        variables: { id: systemId },
        skip: !systemId,
    });
    const systemIotConnection: IoTSystemConnection | undefined = _.get(systemStatusData, 'system.iotConnection') || undefined;

    const { data: systemNetworkData, loading: systemNetworkLoading } = useQuery(SystemNetworkInfo, {
        variables: { id: systemId },
        skip: !systemId || !systemIotConnection?.id,
        // A failed network lookup nulls the whole iotConnection — keep it out of the cache.
        fetchPolicy: 'no-cache',
    });

    const connection: IoTConnection | IoTSystemConnection | undefined = iotConnection ?? systemIotConnection;
    const networkLoading = systemId ? systemNetworkLoading : assetNetworkLoading;
    const signalStrength = systemId
        ? _.get(systemNetworkData, 'system.iotConnection.networkInformation.signalStrength')
        : _.get(assetNetworkData, 'assetLocation.iotConnection.networkInformation.signalStrength');

    return (
        <Box
            ref={ref}
            flexDirection="row"
            display="flex"
            style={style}
            key={node?.id}
            tabIndex={-1}
            data-ib-tree-item=""
            data-selected={selected ? 'true' : undefined}
            data-ib-pinned={pinned ? '' : undefined}
            sx={{
                p: '12px',
                // Keeps the truncated title from ellipsing flush against the
                // open-detail button (only applies when the button is rendered).
                gap: '8px',
                backgroundColor: selected ? '#E5E5E5' : (contextActive ? '#F2F2F2' : '#ffffff'),
                // Halo colour of the favorite-star badge on the type icon — must match
                // the row background in every state so the cut-out stays invisible.
                '--favorite-halo': selected ? '#E5E5E5' : (contextActive ? '#F2F2F2' : '#ffffff'),
                alignItems: 'center',
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                    backgroundColor: selected ? '#E5E5E5' : '#F2F2F2',
                    '--favorite-halo': selected ? '#E5E5E5' : '#F2F2F2',
                },
                '&:focus': {
                    backgroundColor: selected ? '#E5E5E5' : '#F2F2F2',
                    '--favorite-halo': selected ? '#E5E5E5' : '#F2F2F2',
                    outline: 'none',
                },
            }}
            onClick={onClick}
            onKeyDown={onKeyDown}
            onContextMenu={onContextMenu ? (e) => {
                e.preventDefault();
                onContextMenu(e, node);
            } : undefined}
        >
            <Box className="flex-column"
                 sx={{
                     flexGrow: 1,
                     overflow: 'hidden',
                     gap: '4px',
                 }}
            >
                <Box display="flex" flexDirection="column" pl="2px">
                    <Box className="flex-row" sx={{
                        alignContent: "center", gap: '4px', height: '22px',
                    }}>
                        <Tooltip title={getInstalledBaseNodeTypeName(node?.nodeType, t)} placement="top-start" enterDelay={700}
                                 enterNextDelay={500}>
                            <div>
                                <InstalledBaseNodeTypeIcon
                                    nodeTypeKey={node?.nodeType?.key}
                                    size={22}
                                    starred={node?.isStarred}
                                />
                            </div>
                        </Tooltip>
                        <Tooltip title={title || node?.title} placement="top-start" enterDelay={700}
                                 enterNextDelay={500}>
                            <Typography noWrap level="titleSmall">{title || node?.title}</Typography>
                        </Tooltip>
                    </Box>
                    {node.assetLocation?.asset?.serialNumber &&
                        <Typography pl={'4px'} level={'caption'}>
                            {`${t("SN")}: ${node.assetLocation?.asset?.serialNumber}`}
                        </Typography>
                    }
                </Box>
                <Box className="flex-row" sx={{
                    alignItems: "center", gap: '21px',
                }}>
                    <Box className="flex-row" sx={{
                        alignItems: "center", gap: '5px', pl: '12px'
                    }}>
                        <SubNodeArc height={22}/>
                        <Box className="flex-row" sx={{
                            alignItems: "center", gap: '2px'
                        }}>
                            <Graph2Rounded sx={{
                                fontSize: "18px",
                            }}/>
                            <Typography noWrap level="titleSmallLight">{_.get(node, 'childrenNodeCount', '-')}</Typography>
                        </Box>
                        <Box className="flex-row" sx={{
                            alignItems: "center", gap: '2px'
                        }}>
                            <BoxIcon size={18}/>
                            <Typography noWrap level="titleSmallLight">{_.get(node, 'assetCount', '-')}</Typography>
                        </Box>
                    </Box>

                    {connection?.connectionStatus &&
                        <Box className="flex-row" sx={{
                            alignItems: "center", gap: '5px'
                        }}>
                            {statusIconFactory(connection.connectionStatus, 18)}
                            {connection?.id && connection?.connectionStatus && (
                                networkLoading
                                    ? <CircularProgress size={14} />
                                    : <SignalStrengthIcon signalStrength={signalStrength} size={18} />
                            )}
                        </Box>
                    }
                </Box>
            </Box>
            {detailRoute ?
                <IconButton
                    variant="plain"
                    color="primary"
                    className="ib-row-open-route"
                    aria-label={assetLocationId ? t('Open asset details') : t('Open system details')}
                    title={assetLocationId ? t('Open asset details') : t('Open system details')}
                    sx={{
                        "--IconButton-size": "36px",
                        '&:hover': {
                            backgroundColor: '#fff',
                        },
                    }}
                    onClick={(e) => {
                        // Keep the row's onClick (column navigation) from firing too.
                        e.stopPropagation();
                        navigate(detailRoute);
                    }}
                >
                    <OpenInNewRounded fontSize="small"/>
                </IconButton>
            : onOpenDetailView &&
                <IconButton
                    variant="plain"
                    color="primary"
                    className={`ib-row-open-detail${detailOpen ? ' active' : ''}`}
                    aria-label={detailOpen ? t('Close details') : t('Open details')}
                    title={detailOpen ? t('Close details') : t('Open details')}
                    sx={{
                        "--IconButton-size": "36px",
                        // White box (default IconButton radius, like the header
                        // actions) on button hover; the same style stays on
                        // while the node's panel is open (rows behind are grey).
                        backgroundColor: detailOpen ? '#fff' : 'transparent',
                        '&:hover': {
                            backgroundColor: '#fff',
                        },
                    }}
                    onClick={(e) => {
                        // Without this the row's onClick would also fire — and clicking
                        // the already-selected node toggles a deselect (path cut).
                        e.stopPropagation();
                        onOpenDetailView();
                    }}
                >
                    <ReadMoreRounded fontSize="small"/>
                </IconButton>
            }
        </Box>
    );
});

export default InstalledBaseTreeItem;
